import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import AgentSidebar from "./Commun/AgentSidebar";
import { AgentHeader } from "./Commun/AgentHeader";
import { agentPropertylist, agentPreBooking, SaveAgentPreBookingRoomAdd } from "../Services/Service";

export const AgentBooking = () => {
  const navigate = useNavigate();
  const [agentproperty, setAgentproperty] = useState([]);
  const [selectedPropertyID, setSelectedPropertyID] = useState("");
  const [checkInDate, setCheckInDate] = useState("");
  const [checkOutDate, setCheckOutDate] = useState("");
  const [totalRooms, setTotalRooms] = useState(1);
  const [loading, setLoading] = useState(false);

  const [roomDetails, setRoomDetails] = useState([]);
  const [selectedRatePlans, setSelectedRatePlans] = useState({});
  const [selectedMealPlans, setSelectedMealPlans] = useState({});
  const [roomCounts, setRoomCounts] = useState({});
  const [roomOccupancy, setRoomOccupancy] = useState({});
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    setCheckInDate(today.toISOString().split("T")[0]);
    setCheckOutDate(tomorrow.toISOString().split("T")[0]);
  }, []);

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        const res = await agentPropertylist();
        const propsArr = Array.isArray(res) ? res : [];
        setAgentproperty(propsArr);
        if (propsArr.length > 0 && !selectedPropertyID) {
          setSelectedPropertyID(propsArr[0].PropertyID ?? propsArr[0].id);
        }
      } catch (err) {
        console.error("Error fetching properties", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, [selectedPropertyID]);

  const handleCheckInChange = (date) => {
    setCheckInDate(date);
    const next = new Date(date);
    next.setDate(next.getDate() + 1);
    setCheckOutDate(next.toISOString().split("T")[0]);
  };

  const formatAPIDate = (yyyyMmDd) => (yyyyMmDd ? `${yyyyMmDd}T00:00:00` : "");

  const handleSearch = async () => {
    if (!selectedPropertyID || !checkInDate || !checkOutDate) {
      swal("Please select property and dates", { icon: "warning" });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        PropertyID: selectedPropertyID,
        CheckIn: formatAPIDate(checkInDate),
        CheckOut: formatAPIDate(checkOutDate),
        TotalRoom: totalRooms,
      };

      const res = await agentPreBooking(payload);
      const rooms = res?.RoomDetails || [];

      const parsedRooms = rooms.map((r) => ({
        ...r,
        RatePlanParsed: r.RatePlan ? JSON.parse(r.RatePlan) : [],
      }));

      setRoomDetails(parsedRooms);
      setRoomCounts({});
      setSelectedRatePlans({});
      setSelectedMealPlans({});
      setRoomOccupancy({});

      const preBooking = res?.PreBooking?.[0];
      if (preBooking?.PreBookingToken) {
        localStorage.setItem("AgentPreBookingToken", preBooking.PreBookingToken);
        localStorage.setItem("AgentCheckIn", preBooking.Checkin);
        localStorage.setItem("AgentCheckOut", preBooking.checkout);
        localStorage.setItem("AgentPropertyID", preBooking.Property);
      }
    } catch (err) {
      console.error("Error fetching prebooking", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRatePlanChange = (roomId, rateCode, room) => {
    const ratePlan = room.RatePlanParsed.find(rp => rp.Code === rateCode);
    setSelectedRatePlans(prev => ({ ...prev, [roomId]: ratePlan }));
    setSelectedMealPlans(prev => ({ ...prev, [roomId]: null }));
  };

  const handleMealPlanChange = (roomId, mealCode) => {
    const ratePlan = selectedRatePlans[roomId];
    if (!ratePlan) return;
    const mealPlan = ratePlan.MealPlan ? (Array.isArray(ratePlan.MealPlan) ? ratePlan.MealPlan : JSON.parse(ratePlan.MealPlan)) : [];
    const selectedMeal = mealPlan.find(m => m.Code === mealCode);

    setSelectedMealPlans(prev => ({ ...prev, [roomId]: selectedMeal }));
    setRoomOccupancy(prev => ({ ...prev }));
  };

  const handleIncrement = (roomID) => {
    const totalSelected = Object.values(roomCounts).reduce((s, c) => s + c, 0);
    if (totalSelected < totalRooms) {
      setRoomCounts(prev => ({ ...prev, [roomID]: (prev[roomID] || 0) + 1 }));
    }
  };

  const handleDecrement = (roomID) => {
    setRoomCounts(prev => {
      const curr = prev[roomID] || 0;
      if (curr <= 1) {
        const updated = { ...prev };
        delete updated[roomID];
        return updated;
      }
      return { ...prev, [roomID]: curr - 1 };
    });
  };

  const updateOccupancy = (roomID, index, type, action) => {
    const key = `${roomID}-${index}`;
    setRoomOccupancy(prev => {
      const curr = prev[key] || { adults: 2, children: 0, extraBed: 0 };
      const updated = { ...curr, [type]: Math.max(0, curr[type] + (action === "increment" ? 1 : -1)) };
      return { ...prev, [key]: updated };
    });
  };

  const totalSelectedCount = Object.values(roomCounts).reduce((s, c) => s + c, 0);

  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  const calculateRoomPrice = (roomID, index) => {
    const occupancy = roomOccupancy[`${roomID}-${index}`] || { adults: 2, children: 0, extraBed: 0 };
    const meal = selectedMealPlans[roomID]?._RCM?.[0] || {};
    const baseRate = meal.BaseRate || 0;
    const extraAdultRate = meal.ExtraAdultRate || 0;
    const extraChildRate = meal.ExtraChildRate || 0;
    const extraBedCharges = meal.ExtraBedCharges || 0;

    const adultsExtra = Math.max(0, occupancy.adults - 2);
    return baseRate + adultsExtra * extraAdultRate + occupancy.children * extraChildRate + occupancy.extraBed * extraBedCharges;
  };


  // const handleContinueBooking = async () => {
  //   setLoading(true);
  //   const preBookingToken = localStorage.getItem("AgentPreBookingToken");

  //   try {
  //     for (const room of roomDetails) {
  //       const plan = selectedRatePlans[room.RoomID];
  //       const quantity = roomCounts[room.RoomID];

  //       if (plan && quantity) {
  //         for (let i = 0; i < quantity; i++) {
  //           const key = `${room.RoomID}-${i}`;
  //           const occupancy = roomOccupancy[key] || { adults: 2, children: 0, extraBed: 0 };
  //           const mealPlan = selectedMealPlans[room.RoomID];

  //           const formData = new FormData();
  //           formData.append("ID", "0");
  //           formData.append("PreBookingToken", preBookingToken);
  //           formData.append("RoomID", room.RoomID);
  //           formData.append("RatePlanID", plan?.Code || "");
  //           formData.append("MealPlanID", mealPlan?.Code || "");
  //           formData.append("TotalRoom", 1);
  //           formData.append("RoomRate", plan?.PerRoomRate || 0);
  //           formData.append("ExtraBed", occupancy.extraBed);
  //           formData.append("ExtraBedAmount", occupancy.extraBed * (plan?.ExtraBedCharges || 0));
  //           formData.append("Adults", occupancy.adults);
  //           formData.append("Child", occupancy.children);

  //           console.log("FormData to be sent:");
  //           for (let pair of formData.entries()) {
  //             console.log(pair[0], ":", pair[1]);
  //           }

  //           const result = await SaveAgentPreBookingRoomAdd(formData);

  //           if (result?.PreBookingToken) {
  //             swal("Your room booked in hotel!", {
  //               icon: "success",
  //               buttons: {
  //                 confirm: {
  //                   text: "OK",
  //                   className: "btn btn-success",
  //                 },
  //               },
  //             }).then(() => {
  //               navigate("/Property/FinalBooking");
  //             });
  //           } else if (result?.Message) {
  //             swal(result.Message, {
  //               icon: "error",
  //               buttons: {
  //                 confirm: {
  //                   text: "OK",
  //                   className: "btn btn-danger",
  //                 },
  //               },
  //             });
  //             return; // navigate na ho
  //           }
  //         }
  //       } else {
  //         swal("Please select rate plan and quantity for all rooms.", {
  //           icon: "warning",
  //           buttons: {
  //             confirm: { text: "OK", className: "btn btn-warning" },
  //           },
  //         });
  //         return;
  //       }
  //     }
  //   } catch (err) {
  //     if (err.response?.data?.Message) {
  //       alert(err.response.data.Message);
  //     } else {
  //       swal("Something went wrong. Please try again.", {
  //         icon: "error",
  //         buttons: {
  //           confirm: { text: "OK", className: "btn btn-danger" },
  //         },
  //       });
  //     }
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleContinueBooking = async () => {
    setLoading(true);
    const preBookingToken = localStorage.getItem("AgentPreBookingToken");

    try {
      // Filter only selected rooms
      const roomsToBook = roomDetails.filter(room => roomCounts[room.RoomID] > 0 && selectedRatePlans[room.RoomID]);

      if (roomsToBook.length === 0) {
        swal("Please select at least one room with rate plan.", {
          icon: "warning",
          buttons: { confirm: { text: "OK", className: "btn btn-warning" } },
        });
        setLoading(false);
        return;
      }

      for (const room of roomsToBook) {
        const plan = selectedRatePlans[room.RoomID];
        const quantity = roomCounts[room.RoomID];

        for (let i = 0; i < quantity; i++) {
          const key = `${room.RoomID}-${i}`;
          const occupancy = roomOccupancy[key] || { adults: 2, children: 0, extraBed: 0 };
          const mealPlan = selectedMealPlans[room.RoomID];

          const formData = new FormData();
          formData.append("ID", "0");
          formData.append("PreBookingToken", preBookingToken);
          formData.append("RoomID", room.RoomID);
          formData.append("RatePlanID", plan?.Code || "");
          formData.append("MealPlanID", mealPlan?.Code || "");
          formData.append("TotalRoom", 1);
          formData.append("RoomRate", calculateRoomPrice(room.RoomID, i));
          formData.append("ExtraBed", occupancy.extraBed);
          formData.append("ExtraBedAmount", occupancy.extraBed * (plan?.ExtraBedCharges || 0));
          formData.append("Adults", occupancy.adults);
          formData.append("Child", occupancy.children);

          const result = await SaveAgentPreBookingRoomAdd(formData);

          if (!result?.PreBookingToken && result?.Message) {
            swal(result.Message, {
              icon: "error",
              buttons: { confirm: { text: "OK", className: "btn btn-danger" } },
            });
            setLoading(false);
            return;
          }
        }
      }

      // Success after all rooms booked
      swal("Rooms successfully added!", {
        icon: "success",
        buttons: { confirm: { text: "OK", className: "btn btn-success" } },
      }).then(() => navigate("/Agent/BookingConfirm"));
    } catch (err) {
      swal("Something went wrong. Please try again.", {
        icon: "error",
        buttons: { confirm: { text: "OK", className: "btn btn-danger" } },
      });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      <style>{`
       .label-set{
             top: -9px;
              padding-inline: 8px;
              font-weight: bold;
              background: #ffffff;
              left: 23px;
       }
    `}</style>
      {loading && <div className="fullscreen-loader"><div className="fas fa-spinner fa-spin fa-3x" /></div>}
      <div className="wrapper">
        <AgentSidebar />
        <div className="main-panel">
          <AgentHeader />
          <div className="container">
            <div className="page-inner">
              {/* Booking Form */}
              <div className="row">
                <div className="col-md-12">
                  <div className="card">
                    <div className="card-header"><div className="card-title">Agent Booking</div></div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-4">
                          <select className="form-select form-control" value={selectedPropertyID} onChange={e => setSelectedPropertyID(e.target.value)}>
                            <option value="">Select Property</option>
                            {agentproperty.map(p => <option key={p.PropertyID ?? p.id} value={p.PropertyID ?? p.id}>{p.PropertyName ?? p.name}</option>)}
                          </select>
                        </div>
                        <div className="col-md-2 position-relative"><label className="position-absolute label-set" >Form</label><input type="date" className="form-control" value={checkInDate} onChange={e => handleCheckInChange(e.target.value)} /></div>
                        <div className="col-md-2 position-relative"><label className="position-absolute label-set">To</label><input type="date" className="form-control" value={checkOutDate} onChange={e => setCheckOutDate(e.target.value)} min={checkInDate} /></div>
                        <div className="col-md-2 position-relative">
                          <label className="position-absolute label-set">Total Room</label>
                          <select className="form-select form-control" value={totalRooms} onChange={e => setTotalRooms(Number(e.target.value))}>
                            {Array.from(
                              { length: selectedPropertyID ? (agentproperty.find(p => (p.PropertyID ?? p.id) === selectedPropertyID)?.MaxRoomBook || 1) : 1 },
                              (_, i) => (
                                <option key={i + 1} value={i + 1}>{i + 1}</option>
                              )
                            )}
                          </select>
                        </div>
                        <div className="col-md-1 text-end"><button className="btn btn-primary" onClick={handleSearch}>Search</button></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Room List */}
              {roomDetails.length > 0 && <div className="row mt-3">
                <div className="col-md-12">
                  <div className="card">
                    <div className="card-header"><div className="card-title">Room List</div></div>
                    <div className="card-body">
                      <table className="table text-center">
                        <thead>
                          <tr>
                            <th className="text-start">Room Type</th>
                            <th>Room Avl</th>
                            <th style={{ width: "185px" }}>Rate Plan</th>
                            <th style={{ width: "185px" }}>Meal Plan</th>
                            <th>Per Room Rate</th>
                            <th>Quantity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {roomDetails.map(r => {
                            const selectedRate = selectedRatePlans[r.RoomID];
                            const selectedMeal = selectedMealPlans[r.RoomID];
                            const roomQty = roomCounts[r.RoomID] || 0;



                            return (<tr key={r.RoomID}>
                              <td className="text-start">{r.RoomName}</td>
                              <td>{r.count || 0}</td>
                              <td>
                                {r.count > 0 ? (
                                  <select
                                    className="form-select"
                                    value={selectedRate?.Code || ""}
                                    onChange={(e) => handleRatePlanChange(r.RoomID, e.target.value, r)}
                                  >
                                    <option value="">Select Rate Plan</option>
                                    {r.RatePlanParsed.length > 0 ? (
                                      r.RatePlanParsed.map((rp) => (
                                        <option key={rp.Code} value={rp.Code}>{rp.Name}</option>
                                      ))
                                    ) : (
                                      <option disabled>No Rate Plan</option>
                                    )}
                                  </select>
                                ) : (
                                  <span className="text-danger fw-bold">No Room Available</span>
                                )}
                              </td>

                              <td>{selectedRate && <select className="form-select" value={selectedMeal?.Code || ""} onChange={e => handleMealPlanChange(r.RoomID, e.target.value)}>
                                <option value="">Select Meal Plan</option>
                                {(selectedRate?.MealPlan || []).map(mp => <option key={mp.Code} value={mp.Code}>{mp.Name}</option>)}
                              </select>}</td>
                              <td>{selectedMeal ? selectedMeal._RCM?.[0]?.BaseRate ?? 0 : ""}</td>
                              <td>
                                {selectedMeal ? (
                                  roomQty ? (
                                    <div className="hstack gap-2 justify-content-center">
                                      <button
                                        onClick={() => handleDecrement(r.RoomID)}
                                        className="btn btn-outline-secondary btn-sm px-2 py-1"
                                      >
                                        <i className="fas fa-minus"></i>
                                      </button>
                                      <span>{roomQty}</span>
                                      <button
                                        onClick={() => handleIncrement(r.RoomID)}
                                        className="btn btn-outline-secondary btn-sm px-2 py-1"
                                      >
                                        <i className="fas fa-plus"></i>
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      className="btn btn-sm btn-black"
                                      onClick={() => handleIncrement(r.RoomID)}
                                    >
                                      Select
                                    </button>
                                  )
                                ) : (
                                  <span></span>
                                )}
                              </td>
                            </tr>);
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>}

              {/* Continue Booking Button */}
              {totalSelectedCount > 0 && <div className="bg-light border-top text-end px-4 py-3" style={{ position: 'fixed', bottom: 0, left: 0, width: '100%', zIndex: 999 }}>
                <button className="btn btn-black" onClick={() => setShowPopup(true)}>Continue ({totalSelectedCount})</button>
              </div>}

              {/* Popup Modal */}
              {showPopup && (
                <div
                  className="modal d-block"
                  tabIndex="-1"
                  style={{
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1050,
                    padding: "18px",
                  }}
                >
                  <div
                    className="modal-dialog"
                    style={{
                      maxWidth: "600px",
                      width: "100%",
                    }}
                  >
                    <div
                      className="modal-content"
                      style={{
                        borderRadius: "10px",
                        overflow: "hidden",
                      }}
                    >
                      <div className="modal-header">
                        <h5 className="modal-title">Selected Rooms</h5>
                      </div>

                      <div className="modal-body p-3">
                        <div className="d-flex justify-content-between mb-3">
                          <div><strong>From:</strong> {formatDateToDDMMYYYY(checkInDate)}</div>
                          <div><strong>To:</strong> {formatDateToDDMMYYYY(checkOutDate)}</div>
                        </div>

                        {roomDetails
                          .filter(room => roomCounts[room.RoomID])
                          .flatMap((room) => {
                            const quantity = roomCounts[room.RoomID] || 0;

                            return Array.from({ length: quantity }, (_, i) => {
                              const key = `${room.RoomID}-${i}`;
                              const occupancy = roomOccupancy[key] || { adults: 2, children: 0, extraBed: 0 };

                              return (
                                <div key={key} className="bg-light p-3 rounded mb-3 position-relative">
                                  <div className="d-flex justify-content-between mb-2">
                                    <strong>{room.RoomName}</strong>
                                    <div>
                                      <span>Rate Plan: <strong className="text-danger">{selectedRatePlans[room.RoomID]?.Name}</strong></span>
                                      <br />
                                      <span>Meal Plan: <strong className="text-danger">{selectedMealPlans[room.RoomID]?.Name}</strong></span>
                                    </div>
                                  </div>

                                  {/* Occupancy Controls */}
                                  <div className="row">
                                    <div className="col-md-2">
                                      <label className="fw-bold">Adults</label>
                                      <div className="hstack gap-2 mb-2">
                                        <button onClick={() => updateOccupancy(room.RoomID, i, 'adults', 'decrement')} className="btn btn-outline-secondary btn-sm py-1 px-2">
                                          <i className="fas fa-minus"></i>
                                        </button>
                                        <span>{occupancy.adults}</span>
                                        <button onClick={() => updateOccupancy(room.RoomID, i, 'adults', 'increment')} className="btn btn-outline-secondary btn-sm py-1 px-2">
                                          <i className="fas fa-plus"></i>
                                        </button>
                                      </div>
                                    </div>

                                    <div className="col-md-2">
                                      <label className="fw-bold">Children</label>
                                      <div className="hstack gap-2 mb-2">
                                        <button onClick={() => updateOccupancy(room.RoomID, i, 'children', 'decrement')} className="btn btn-outline-secondary btn-sm py-1 px-2">
                                          <i className="fas fa-minus"></i>
                                        </button>
                                        <span>{occupancy.children}</span>
                                        <button onClick={() => updateOccupancy(room.RoomID, i, 'children', 'increment')} className="btn btn-outline-secondary btn-sm py-1 px-2">
                                          <i className="fas fa-plus"></i>
                                        </button>
                                      </div>
                                    </div>

                                    <div className="col-md-3">
                                      <label className="fw-bold">Extra Bed</label>
                                      <div className="hstack gap-2 mb-2">
                                        <button onClick={() => updateOccupancy(room.RoomID, i, 'extraBed', 'decrement')} className="btn btn-outline-secondary btn-sm py-1 px-2">
                                          <i className="fas fa-minus"></i>
                                        </button>
                                        <span>{occupancy.extraBed}</span>
                                        <button onClick={() => updateOccupancy(room.RoomID, i, 'extraBed', 'increment')} className="btn btn-outline-secondary btn-sm py-1 px-2">
                                          <i className="fas fa-plus"></i>
                                        </button>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="fw-bold fs-5 position-absolute" style={{ bottom: "10px", right: "15px" }}>
                                    ₹ {calculateRoomPrice(room.RoomID, i)}
                                  </div>
                                </div>
                              );
                            });
                          })}
                      </div>

                      <div className="modal-footer">
                        <button type="button" className="btn btn-danger" onClick={() => setShowPopup(false)}>Close</button>
                        <button type="button" className="btn btn-primary" onClick={handleContinueBooking}>Continue</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
